import React, { useState, useCallback, useMemo } from "react";
import { fetchAPI } from "../../../utils/api";
import { Box, Card, CardContent, Typography, CircularProgress, Alert } from "@mui/material";
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

import { Currency } from "../../interfaces/OperatorDashboard";
import { PendingPayoutInvoicesHeader } from "./PendingPayoutInvoicesHeader";
import { PendingPayoutInvoicesKPIs } from "./PendingPayoutInvoicesKPIs";
import { PendingPayoutInvoicesData } from "./PendingPayoutInvoicesData";

type PendingPayoutInvoicesTableProps = {
    currency: Currency;
    institutionId: string;
};

export const PendingPayoutInvoicesTable = ({ currency, institutionId }: PendingPayoutInvoicesTableProps) => {
    const [hasSearched, setHasSearched] = useState(false);
    const [invoices, setInvoices] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");

    const [fromDate, setFromDate] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
    const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);

    const hasMultipleOperators = currency === 'EUR';

    const handleSearchInvoices = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        setHasSearched(true);
        
        try {
            const springFrom = `${fromDate}T00:00:00`;
            const springTo = `${toDate}T23:59:59`;
            const res = await fetchAPI(`/invoices/${currency}?from=${springFrom}&to=${springTo}`, { 
                headers: { 'institution': institutionId } 
            });
            if (!res.ok) throw new Error(`Error: ${res.status}`);
            const data = await res.json();
            setInvoices(Array.isArray(data) ? data : []);
            setPage(0); 
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [currency, institutionId, fromDate, toDate]);

    const filteredInvoices = useMemo(() => {
        return invoices.filter(inv => 
            inv.id?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [invoices, searchTerm]);

    const stats = useMemo(() => {
        let globalAwaited = 0;
        const opStats: Record<string, { awaited: number, paid: number, percentage: number }> = {};

        filteredInvoices.forEach(inv => {
            const detailsMap = inv.operator_details || {};
            Object.entries(detailsMap).forEach(([opName, d]: [string, any]) => {
                if (!opStats[opName]) opStats[opName] = { awaited: 0, paid: 0, percentage: 0 };
                const amount = d?.amount || 0;
                
                if (d?.paid) {
                    opStats[opName].paid += amount;
                } else {
                    opStats[opName].awaited += amount;
                    globalAwaited += amount;
                }
            });
        });

        if (globalAwaited > 0) {
            Object.values(opStats).forEach(op => {
                op.percentage = (op.awaited / globalAwaited) * 100;
            });
        }

        return { totalInvoices: filteredInvoices.length, globalAwaited, opStats };
    }, [filteredInvoices]);

    return (
        <Card variant="outlined" sx={{ mb: 6, borderRadius: 4, borderColor: '#cbd5e1', bgcolor: '#fff' }}>
            {/* SEARCH */}
            <PendingPayoutInvoicesHeader 
                currency={currency}
                fromDate={fromDate}
                setFromDate={setFromDate}
                toDate={toDate}
                setToDate={setToDate}
                onSearch={handleSearchInvoices}
                isLoading={isLoading}
            />

            <CardContent sx={{ p: 3 }}>
                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                {isLoading ? (
                    <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress /></Box>
                ) : !hasSearched ? (
                    <Box sx={{ py: 10, textAlign: 'center', color: 'text.disabled' }}>
                        <ReceiptLongIcon sx={{ fontSize: 60, mb: 2, opacity: 0.2 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>Ready to search</Typography>
                        <Typography variant="body2">Select a date range and click Search to load invoices.</Typography>
                    </Box>
                ) : (
                    <Box>
                        {/* STATS */}
                        <PendingPayoutInvoicesKPIs 
                            stats={stats} 
                            currency={currency} 
                            hasMultipleOperators={hasMultipleOperators} 
                        />
                        
                        {/* Invoices List */}
                        <PendingPayoutInvoicesData 
                            filteredInvoices={filteredInvoices}
                            currency={currency}
                            hasMultipleOperators={hasMultipleOperators}
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            page={page}
                            setPage={setPage}
                            rowsPerPage={rowsPerPage}
                            setRowsPerPage={setRowsPerPage}
                        />
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};